import React, { useEffect, useMemo, useState } from 'react';
import { promptDialog } from '../components/ui/ImpulzaDialog';
import { BILLING_BRAND } from '../data/billingBrandConfig';

// Nombre de marca por app (Impulza / Gastify). billingBrandConfig esta
// PROTECTED en el sync, asi que cada app conserva su brandName sin
// hardcodear "Impulza" aca.
const BRAND_NAME = BILLING_BRAND?.brandName || 'el sistema';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft,
  Ban,
  Calendar,
  CheckCircle2,
  ClipboardList,
  Layers,
  Package,
  Printer,
  ShoppingBag,
  User2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/numberFormat';

const getUserLabel = (userRow) => userRow?.full_name || userRow?.email || 'Usuario';

function ProduccionHistorialDetalle({ onNavigate }) {
  const { profile } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [orden, setOrden] = useState(null);
  const [profilesMap, setProfilesMap] = useState(new Map());
  // FIX IMPORTANTE #14: estado para la receta vigente y comparar contra el snapshot
  const [recetaVigente, setRecetaVigente] = useState(null);
  const [costoVigente, setCostoVigente] = useState(null);
  // FIX SERVER-SIDE: lista de prorrateos desde la tabla dedicada (no más parser de observaciones)
  const [prorrateosTabla, setProrrateosTabla] = useState([]);
  const [reversingProrrateoId, setReversingProrrateoId] = useState(null);

  useEffect(() => {
    if (!profile?.organization_id || !id) return;
    fetchDetalle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id, id]);

  const fetchDetalle = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('produccion')
        .select(`
          *,
          sitios:sitio_id (nombre)
        `)
        .eq('organization_id', profile.organization_id)
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('No encontramos la producción solicitada.');

      setOrden(data);

      // (profileIds completos se construyen DESPUÉS de cargar prorrateos para
      // incluir también los usuarios que aplicaron/revirtieron prorrateos)
      let baseProfileIds = [data.created_by, data.anulado_por].filter(Boolean);

      // FIX IMPORTANTE #14: traemos la receta vigente del producto para comparar
      // contra el snapshot guardado en `ingredientes_usados`. Avisamos al usuario
      // si los costos/ingredientes cambiaron desde que se hizo la producción.
      if (data.producto_id) {
        const [recetaRes, prodRes, prorrateosRes] = await Promise.all([
          supabase
            .from('recetas')
            .select('ingredientes, rendimiento, updated_at')
            .eq('producto_id', data.producto_id)
            .eq('organization_id', profile.organization_id)
            .maybeSingle(),
          supabase
            .from('products')
            .select('cost_price')
            .eq('id', data.producto_id)
            .eq('organization_id', profile.organization_id)
            .maybeSingle(),
          // FIX SERVER-SIDE: cargamos prorrateos de la tabla dedicada.
          // Si la tabla aún no existe (migración pendiente), queda en [] y caemos
          // al parser legacy de observaciones.
          supabase
            .from('produccion_prorrateos')
            .select('id, concepto, monto_unitario, monto_total, aplicado_por, aplicado_en, revertido_en, revertido_por, motivo_reversa, metadata')
            .eq('produccion_id', data.id)
            .eq('organization_id', profile.organization_id)
            .order('aplicado_en', { ascending: false }),
        ]);
        setRecetaVigente(recetaRes?.data || null);
        setCostoVigente(prodRes?.data ? Number(prodRes.data.cost_price || 0) : null);

        let prorrateosList = [];
        if (prorrateosRes?.error && prorrateosRes.error.code === 'PGRST205') {
          setProrrateosTabla([]);
        } else if (prorrateosRes?.error) {
          console.warn('[HistorialDetalle] Error cargando prorrateos:', prorrateosRes.error);
          setProrrateosTabla([]);
        } else {
          prorrateosList = prorrateosRes?.data || [];
          setProrrateosTabla(prorrateosList);
        }

        // Incluimos usuarios de prorrateos en el set para hidratar profilesMap
        prorrateosList.forEach((p) => {
          if (p.aplicado_por) baseProfileIds.push(p.aplicado_por);
          if (p.revertido_por) baseProfileIds.push(p.revertido_por);
        });
      }

      const profileIds = Array.from(new Set(baseProfileIds));
      if (profileIds.length > 0) {
        const profilesRes = await supabase
          .from('profiles')
          .select('id, full_name, email')
          .in('id', profileIds);

        if (profilesRes.error) throw profilesRes.error;
        setProfilesMap(new Map((profilesRes.data || []).map((row) => [row.id, row])));
      } else {
        setProfilesMap(new Map());
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'No se pudo cargar el detalle de producción');
    } finally {
      setLoading(false);
    }
  };

  // FIX IMPORTANTE #22: respeta locale/currency de la organización
  const fmtMoney = (value) => formatCurrency(value, { orgId: profile?.organization_id });

  // FIX IMPORTANTE #14: análisis de divergencia snapshot vs receta vigente
  const recetaDivergence = useMemo(() => {
    if (!orden || !recetaVigente) return null;

    const snapshotIngs = Array.isArray(orden.ingredientes_usados) ? orden.ingredientes_usados : [];
    const vigenteIngs = Array.isArray(recetaVigente.ingredientes) ? recetaVigente.ingredientes : [];

    const snapMap = new Map(snapshotIngs.map((i) => [String(i.id || ''), i]));
    const vigMap = new Map(vigenteIngs.map((i) => [String(i.id || ''), i]));

    const diffs = [];
    // Insumos cuyo costo unitario cambió
    snapshotIngs.forEach((s) => {
      const v = vigMap.get(String(s.id || ''));
      if (!v) {
        diffs.push({ tipo: 'insumo_eliminado', nombre: s.nombre, id: s.id });
      } else if (Math.abs(Number(s.costoUnitarioInsumo || 0) - Number(v.costo_unitario || 0)) > 0.01) {
        diffs.push({
          tipo: 'costo_cambiado',
          nombre: s.nombre,
          costoSnapshot: Number(s.costoUnitarioInsumo || 0),
          costoVigente: Number(v.costo_unitario || 0),
        });
      }
    });
    // Insumos nuevos en la receta vigente que no estaban en el snapshot
    vigenteIngs.forEach((v) => {
      if (!snapMap.has(String(v.id || ''))) {
        diffs.push({ tipo: 'insumo_agregado', nombre: v.nombre, id: v.id });
      }
    });

    const costoSnapshot = Number(orden.costo_unitario || 0);
    const costoActualPT = costoVigente !== null ? costoVigente : null;
    const cambioCostoPT = costoActualPT !== null
      ? Math.abs(costoSnapshot - costoActualPT) > 0.01
      : false;

    return {
      hayCambios: diffs.length > 0 || cambioCostoPT,
      diffs,
      costoSnapshot,
      costoActualPT,
      cambioCostoPT,
      ultimaActualizacionReceta: recetaVigente.updated_at || null,
    };
  }, [orden, recetaVigente, costoVigente]);

  const ingredientes = useMemo(
    () => (Array.isArray(orden?.ingredientes_usados) ? orden.ingredientes_usados : []),
    [orden?.ingredientes_usados],
  );

  const stats = useMemo(() => {
    const totalIngredientes = ingredientes.length;
    const cantidadTotal = ingredientes.reduce((acc, item) => acc + Number(item?.necesario || 0), 0);
    const costoIngredientes = ingredientes.reduce(
      (acc, item) => acc + Number(item?.costoUnitarioInsumo || 0) * Number(item?.necesario || 0),
      0,
    );

    return { totalIngredientes, cantidadTotal, costoIngredientes };
  }, [ingredientes]);

  const handleBack = () => {
    if (typeof onNavigate === 'function') {
      onNavigate('produccion-historial');
      return;
    }
    navigate('/sistema/produccion-historial');
  };

  // FIX CRÍTICO #9: escapar HTML para evitar XSS al imprimir.
  const escapeHTML = (value) => {
    if (value === null || value === undefined) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const handlePrint = () => {
    if (!orden) return;

    const ventana = window.open('', '_blank');
    if (!ventana) return;

    const idCorto = escapeHTML(String(orden.id || '').slice(0, 8));
    const productoNombre = escapeHTML(orden.producto_nombre || '');
    const cantidad = escapeHTML(orden.cantidad || 0);
    const fecha = escapeHTML(orden.fecha ? new Date(orden.fecha).toLocaleString() : '');
    const sitio = escapeHTML(orden.sitios?.nombre || 'General');
    const filasInsumos = ingredientes
      .map((ing) => `
        <tr>
          <td>${escapeHTML(ing.nombre || 'Insumo')}</td>
          <td>${escapeHTML(ing.necesario || 0)} ${escapeHTML(ing.um || '')}</td>
          <td>${escapeHTML(fmtMoney((ing.costoUnitarioInsumo || 0) * (ing.necesario || 0)))}</td>
        </tr>
      `)
      .join('');

    ventana.document.write(`
      <html>
        <head>
          <title>Orden de Produccion #${idCorto}</title>
          <style>
            body { font-family: sans-serif; padding: 40px; color: #333; }
            .header { border-bottom: 2px solid #eee; padding-bottom: 20px; margin-bottom: 30px; }
            .title { font-size: 24px; font-weight: bold; text-transform: uppercase; }
            .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px; }
            .label { font-size: 10px; color: #888; text-transform: uppercase; font-weight: bold; }
            .value { font-size: 14px; font-weight: bold; margin-top: 5px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th { text-align: left; font-size: 10px; text-transform: uppercase; color: #888; border-bottom: 1px solid #eee; padding: 10px; }
            td { padding: 10px; border-bottom: 1px solid #f9f9f9; font-size: 12px; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="title">Comprobante de Produccion</div>
            <p>${BRAND_NAME} Enterprise - Gestion de Fabrica</p>
          </div>
          <div class="meta">
            <div><div class="label">Producto</div><div class="value">${productoNombre}</div></div>
            <div><div class="label">Cantidad</div><div class="value">${cantidad}</div></div>
            <div><div class="label">Fecha</div><div class="value">${fecha}</div></div>
            <div><div class="label">Sitio</div><div class="value">${sitio}</div></div>
          </div>
          <div class="label">Insumos Utilizados</div>
          <table>
            <thead><tr><th>Ingrediente</th><th>Cantidad</th><th>Costo subtotal</th></tr></thead>
            <tbody>
              ${filasInsumos}
            </tbody>
          </table>
        </body>
      </html>
    `);
    ventana.document.close();
    ventana.print();
  };

  if (loading) {
    return (
      <div className="imp-page imp-container animate-nova-in">
        <div className="flex flex-col items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-slate-200 border-t-[#51A7FD] rounded-full animate-spin"></div>
          <p className="mt-4 text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">
            Cargando detalle...
          </p>
        </div>
      </div>
    );
  }

  if (!orden) {
    return (
      <div className="imp-page imp-container animate-nova-in">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 text-center">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
            No se encontró la producción.
          </p>
          <button
            type="button"
            onClick={handleBack}
            className="mt-4 px-4 py-2 rounded-xl bg-[#0B2A36] text-white text-[10px] font-black uppercase tracking-widest"
          >
            Volver
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="imp-page imp-container animate-nova-in">
      <div className="imp-header mb-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={handleBack}
              className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-[#51A7FD] shadow-sm transition-all"
              title="Volver al historial"
            >
              <ArrowLeft size={18} />
            </button>
            <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
              <ClipboardList size={18} className="text-[#3FF2AA]" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">
                Detalle de Producción
              </h1>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                Orden #{String(orden.id || '').slice(0, 8)}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handlePrint}
            className="p-2 bg-white border border-slate-200 text-slate-400 hover:text-[#51A7FD] hover:border-blue-200 rounded-xl transition-all shadow-sm"
            title="Imprimir comprobante"
          >
            <Printer size={16} />
          </button>
        </div>
      </div>

      {/* FIX IMPORTANTE #14: banner de divergencia con la receta vigente.
          Aclara al usuario que los costos mostrados son un SNAPSHOT al momento
          de la producción, y avisa si la receta cambió desde entonces. */}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 mb-3 flex items-start gap-3">
        <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
          <ClipboardList size={14} className="text-amber-700" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[9px] font-black uppercase tracking-widest text-amber-700">
            Snapshot al {new Date(orden.created_at || orden.fecha).toLocaleDateString('es-AR')}
          </p>
          <p className="text-[10px] text-amber-900 mt-0.5">
            Los costos e insumos mostrados son los que se usaron en la producción original.
            {recetaDivergence?.hayCambios && ' La receta vigente cambió desde entonces:'}
          </p>
          {recetaDivergence?.hayCambios && (
            <ul className="mt-2 space-y-1 text-[10px] text-amber-900">
              {recetaDivergence.cambioCostoPT && (
                <li>
                  • Costo unitario producto: snapshot {fmtMoney(recetaDivergence.costoSnapshot)} → vigente {fmtMoney(recetaDivergence.costoActualPT)}
                </li>
              )}
              {recetaDivergence.diffs.slice(0, 5).map((d, i) => (
                <li key={i}>
                  {d.tipo === 'costo_cambiado' && (
                    <>• {d.nombre}: {fmtMoney(d.costoSnapshot)} → {fmtMoney(d.costoVigente)}</>
                  )}
                  {d.tipo === 'insumo_eliminado' && <>• Insumo eliminado de la receta: {d.nombre}</>}
                  {d.tipo === 'insumo_agregado' && <>• Insumo agregado a la receta: {d.nombre}</>}
                </li>
              ))}
              {recetaDivergence.diffs.length > 5 && (
                <li>… y {recetaDivergence.diffs.length - 5} cambio(s) más</li>
              )}
            </ul>
          )}
        </div>
      </div>

      <div className="imp-kpis mb-3">
        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Producto</p>
            <Package size={16} className="text-slate-300" />
          </div>
          <p className="mt-1.5 text-base font-black text-slate-800 tracking-tight uppercase">
            {orden.producto_nombre}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
            {Number(orden.cantidad || 0)} unidades
          </p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Ingredientes</p>
            <Layers size={16} className="text-slate-300" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{stats.totalIngredientes}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
            {stats.cantidadTotal.toLocaleString('es-AR')} unidades insumo
          </p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Estado</p>
            {orden.activo ? (
              <CheckCircle2 size={16} className="text-emerald-500" />
            ) : (
              <Ban size={16} className="text-rose-500" />
            )}
          </div>
          <p className={`mt-1.5 text-2xl font-black tracking-tight ${orden.activo ? 'text-emerald-600' : 'text-rose-500'}`}>
            {orden.activo ? 'OK' : 'ANULADA'}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
            Costo lote {fmtMoney(orden.costo_total)}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-1 space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <div className="flex items-center gap-2 border-b border-slate-100 pb-3 mb-4">
              <Calendar className="text-[#51A7FD]" size={15} />
              <span className="text-[9px] font-black text-slate-700 uppercase tracking-widest">Datos generales</span>
            </div>

            <div className="space-y-4">
              <div>
                <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest block">Fecha/Hora</span>
                <span className="text-[10px] font-black text-slate-800 block mt-1">{new Date(orden.fecha).toLocaleString()}</span>
              </div>
              <div>
                <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest block">Sitio</span>
                <span className="text-[10px] font-black text-slate-800 uppercase block mt-1">{orden.sitios?.nombre || 'General'}</span>
              </div>
              <div>
                <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest block">Cantidad producida</span>
                <span className="text-[10px] font-black text-[#51A7FD] block mt-1">{orden.cantidad} UN</span>
              </div>
              <div>
                <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest block">Costo total</span>
                <span className="text-[10px] font-black text-slate-800 block mt-1">{fmtMoney(orden.costo_total)}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <div className="flex items-center gap-2 border-b border-slate-100 pb-3 mb-4">
              <User2 className="text-[#51A7FD]" size={15} />
              <span className="text-[9px] font-black text-slate-700 uppercase tracking-widest">Auditoría</span>
            </div>

            <div className="space-y-4">
              <div>
                <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest block">Cargó</span>
                <span className="text-[10px] font-black text-slate-800 block mt-1">{getUserLabel(profilesMap.get(orden.created_by))}</span>
              </div>
              <div>
                <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest block">Anuló</span>
                <span className="text-[10px] font-black text-slate-800 block mt-1">
                  {orden.activo ? 'Sin anulación' : getUserLabel(profilesMap.get(orden.anulado_por))}
                </span>
              </div>
              {!orden.activo ? (
                <>
                  <div>
                    <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest block">Fecha anulación</span>
                    <span className="text-[10px] font-black text-slate-800 block mt-1">
                      {orden.anulado_el ? new Date(orden.anulado_el).toLocaleString() : '-'}
                    </span>
                  </div>
                  <div>
                    <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest block">Motivo</span>
                    <span className="text-[10px] font-black text-rose-600 block mt-1">{orden.anulacion_motivo || '-'}</span>
                  </div>
                </>
              ) : null}
            </div>
          </div>

          {orden.observaciones ? (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest block">Observaciones</span>
              <div className="mt-3 p-4 bg-blue-50/50 border border-blue-100 rounded-2xl text-[10px] font-bold text-slate-600 uppercase">
                {orden.observaciones}
              </div>
            </div>
          ) : null}

          {/* FIX UX #33 + SERVER-SIDE: prorrateos aplicados a esta producción.
              Modo TABLA (preferido): lee de `produccion_prorrateos` con metadata
              completa y botón de reversa. Modo LEGACY (fallback): parsea las
              marcas [Prorrateo: ...] embebidas en observaciones. */}
          {(() => {
            // Modo TABLA: si tenemos datos de la tabla dedicada, los usamos
            if (prorrateosTabla.length > 0) {
              const activos = prorrateosTabla.filter((p) => !p.revertido_en);
              const revertidos = prorrateosTabla.filter((p) => p.revertido_en);
              const totalUnitActivos = activos.reduce((a, p) => a + Number(p.monto_unitario || 0), 0);
              const totalLoteActivos = activos.reduce((a, p) => a + Number(p.monto_total || 0), 0);

              return (
                <div className="bg-white rounded-2xl border border-amber-200 shadow-sm p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[8px] font-black text-amber-700 uppercase tracking-widest">
                      Prorrateos aplicados
                    </span>
                    <span className="text-[9px] font-black text-amber-700">
                      {activos.length} activo(s) · +{fmtMoney(totalLoteActivos)} al lote
                    </span>
                  </div>
                  <ul className="space-y-2">
                    {activos.map((p) => (
                      <li
                        key={p.id}
                        className="flex justify-between items-center bg-amber-50/60 px-3 py-2 rounded-xl border border-amber-100"
                      >
                        <div className="flex flex-col min-w-0">
                          <span className="text-[10px] font-bold text-slate-700 uppercase">{p.concepto}</span>
                          <span className="text-[8px] text-slate-400 font-bold">
                            {p.aplicado_en ? new Date(p.aplicado_en).toLocaleDateString('es-AR') : ''}
                            {p.aplicado_por && profilesMap.get(p.aplicado_por)
                              ? ` · ${getUserLabel(profilesMap.get(p.aplicado_por))}`
                              : ''}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-[10px] font-black text-amber-700">
                            +{fmtMoney(p.monto_unitario)}/u
                          </span>
                          {orden.activo && (
                            <button
                              type="button"
                              disabled={reversingProrrateoId === p.id}
                              onClick={async () => {
                                const motivo = await promptDialog({
                                  title: 'Revertir prorrateo',
                                  message: 'Se quita este prorrateo de la orden y se recalcula su costo.',
                                  label: 'Motivo de la reversa',
                                  defaultValue: 'Reversa manual',
                                  required: true,
                                  tone: 'danger',
                                  confirmLabel: 'Revertir',
                                });
                                if (motivo === null) return;
                                setReversingProrrateoId(p.id);
                                try {
                                  const res = await supabase.rpc('reverse_production_prorrateo', {
                                    p_prorrateo_id: p.id,
                                    p_reason: motivo,
                                  });
                                  if (res.error) throw res.error;
                                  toast.success('Prorrateo revertido');
                                  fetchDetalle();
                                } catch (err) {
                                  console.error(err);
                                  toast.error('Error al revertir: ' + (err?.message || ''));
                                } finally {
                                  setReversingProrrateoId(null);
                                }
                              }}
                              className="text-[8px] font-black uppercase tracking-widest px-2 py-1 rounded-lg bg-white border border-amber-200 text-amber-700 hover:bg-amber-100 disabled:opacity-50"
                              title="Revertir prorrateo (devuelve el costo agregado)"
                            >
                              Revertir
                            </button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                  {revertidos.length > 0 && (
                    <details className="mt-3">
                      <summary className="text-[8px] font-bold uppercase tracking-widest text-slate-400 cursor-pointer">
                        Ver {revertidos.length} prorrateo(s) revertido(s)
                      </summary>
                      <ul className="mt-2 space-y-1">
                        {revertidos.map((p) => (
                          <li
                            key={p.id}
                            className="flex justify-between items-center bg-slate-50 px-3 py-2 rounded-xl border border-slate-100 opacity-60"
                          >
                            <div className="flex flex-col min-w-0">
                              <span className="text-[9px] font-bold text-slate-500 uppercase line-through">{p.concepto}</span>
                              <span className="text-[8px] text-slate-400 font-bold">
                                Revertido {p.revertido_en ? new Date(p.revertido_en).toLocaleDateString('es-AR') : ''}
                                {p.motivo_reversa ? ` · ${p.motivo_reversa}` : ''}
                              </span>
                            </div>
                            <span className="text-[9px] text-slate-400 line-through">
                              {fmtMoney(p.monto_unitario)}/u
                            </span>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              );
            }

            // Modo LEGACY: parser de observaciones (fallback si la tabla está vacía
            // pero hay marcas embebidas — datos pre-migración)
            const matches = String(orden.observaciones || '').match(/\[Prorrateo:[^\]]*\]/g) || [];
            if (matches.length === 0) return null;
            const items = matches.map((m) => {
              const sin = m.replace('[Prorrateo:', '').replace(']', '').trim();
              const partes = sin.split('|').map((s) => s.trim());
              const concepto = partes[0] || '—';
              const montoMatch = (partes[1] || '').match(/\$\s*([\d.,]+)/);
              const monto = montoMatch ? Number(montoMatch[1].replace(',', '.')) || 0 : 0;
              return { concepto, monto };
            });
            const totalUnit = items.reduce((a, i) => a + i.monto, 0);
            const totalLote = totalUnit * Number(orden.cantidad || 0);
            return (
              <div className="bg-white rounded-2xl border border-amber-200 shadow-sm p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[8px] font-black text-amber-700 uppercase tracking-widest">
                    Prorrateos aplicados (modo legacy)
                  </span>
                  <span className="text-[9px] font-black text-amber-700">
                    {items.length} cargo(s) · +{fmtMoney(totalLote)} al lote
                  </span>
                </div>
                <ul className="space-y-2">
                  {items.map((it, i) => (
                    <li key={i} className="flex justify-between items-center bg-amber-50/60 px-3 py-2 rounded-xl border border-amber-100">
                      <span className="text-[10px] font-bold text-slate-700 uppercase">{it.concepto}</span>
                      <span className="text-[10px] font-black text-amber-700">+{fmtMoney(it.monto)}/u</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-[8px] text-slate-400 italic">
                  Estos prorrateos están en formato legacy. Aplicá la migración 20260517 para migrarlos a la tabla nueva con reversa auditable.
                </p>
              </div>
            );
          })()}
        </div>

        <div className="xl:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShoppingBag size={16} className="text-[#3FF2AA]" />
              <span className="text-[10px] font-black text-white uppercase tracking-widest">Ingredientes usados</span>
            </div>
            <span className="text-[9px] font-bold text-white/50 uppercase tracking-widest">
              {ingredientes.length} items
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[760px]">
              <thead>
                <tr className="bg-slate-50/60 border-b border-slate-100 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                  <th className="py-3 px-5">Ingrediente</th>
                  <th className="py-3 px-4 text-center">Cantidad usada</th>
                  <th className="py-3 px-4 text-center">Unidad</th>
                  <th className="py-3 px-4 text-right">Costo unitario</th>
                  <th className="py-3 px-5 text-right">Costo subtotal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {ingredientes.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="py-16 text-center">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-300">
                        Esta producción no tiene ingredientes registrados.
                      </p>
                    </td>
                  </tr>
                ) : (
                  ingredientes.map((ing, index) => (
                    <tr key={`${ing.id || ing.nombre || 'ing'}-${index}`} className="hover:bg-blue-50/20 transition-all">
                      <td className="py-3 px-5">
                        <div className="flex flex-col">
                          <span className="text-[10px] font-black uppercase text-slate-800 tracking-tight">
                            {ing.nombre || 'Insumo'}
                          </span>
                          {ing.id ? (
                            <span className="text-[8px] font-bold uppercase text-slate-400 mt-1">
                              ID {String(ing.id).slice(0, 8)}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="text-[10px] font-black text-[#51A7FD]">{Number(ing.necesario || 0).toLocaleString('es-AR')}</span>
                      </td>
                      <td className="py-3 px-4 text-center text-[10px] font-bold text-slate-500 uppercase">
                        {ing.um || '-'}
                      </td>
                      <td className="py-3 px-4 text-right text-[10px] font-black text-slate-700">
                        {fmtMoney(ing.costoUnitarioInsumo)}
                      </td>
                      <td className="py-3 px-5 text-right text-[10px] font-black text-slate-900">
                        {fmtMoney(Number(ing.costoUnitarioInsumo || 0) * Number(ing.necesario || 0))}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProduccionHistorialDetalle;
